/**
 * Assessment Utility Functions
 * Helper functions for assessment-related operations
 */

import { Assessment } from '@/hooks/useAssessments';
import { AssessmentPurposeLabels } from '@app/types';

/**
 * Generate assessment title using consistent logic across all components
 *
 * If assessment has a custom title, use it.
 * Otherwise, auto-generate: "{purpose} Assessment - {date}"
 *
 * @param assessment - The assessment object
 * @returns Formatted assessment title
 *
 * @example
 * // With custom title
 * getAssessmentTitle({ title: "My Assessment", ... })
 * // Returns: "My Assessment"
 *
 * // Without custom title
 * getAssessmentTitle({ title: null, assessmentPurpose: "Fundraising Preparation", createdAt: "2026-01-15", ... })
 * // Returns: "Fundraising Preparation Assessment - Jan 15, 2026"
 */
export function getAssessmentTitle(assessment: Assessment & { title?: string | null }): string {
  // Auto-generate default title: "{purpose} Assessment - {date}"
  const date = new Date(assessment.createdAt).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
  const purposeLabel = AssessmentPurposeLabels[assessment.assessmentPurpose as keyof typeof AssessmentPurposeLabels] ?? assessment.assessmentPurpose;
  const defaultTitle = `${purposeLabel} Assessment - ${date}`;

  // Return custom title if provided, otherwise use auto-generated
  // Using optional chaining since title is nullable in OpenAPI schema
  return assessment.title?.trim() || defaultTitle;
}
